import { DndContext, DragOverlay } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Button,
  Div,
  ErrorToast,
  H3,
  Icon,
  Img,
  P,
  PlusIcon,
  noCategories,
} from '@vgrubs/components';
import {
  BTCategoryResponse,
  BTMenuResponse,
} from 'components/src/types/brand.types';
import { Dispatch, SetStateAction, useState } from 'react';
import { AddCategoryModal } from './AddCategoryModal';
import { CategoryComponent } from './CategoryComponent';
import { DeleteCategoryModal } from './DeleteCategoryModal';

export const Categories = ({
  menu,
  setMenu,
}: {
  menu: BTMenuResponse | null;
  setMenu: Dispatch<SetStateAction<BTMenuResponse | null>>;
}) => {
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState<BTCategoryResponse | null>(null);
  const [deleteModal, setDeleteModal] = useState<number | null>(null);

  async function handleEditCategory(
    name: BTCategoryResponse['name'],
    description: BTCategoryResponse['description'],
    minItems: BTCategoryResponse['minItems'],
    maxItems: BTCategoryResponse['maxItems'],
    emptyForm: () => void
  ) {
    const body = {
      name,
      description,
      minItems,
      maxItems,
    };

    if (!name) return ErrorToast('Please enter category name');
    if (!editModal) return;
    if (minItems && minItems > editModal.items.length)
      return ErrorToast(
        'Minimum items cannot be greater than the number of items in the category'
      );
    if (minItems && maxItems && minItems > maxItems)
      return ErrorToast('Minimum items cannot be greater than maximum items');

    setMenu((prev) => {
      if (!prev) return prev;
      const updated = prev.categories.map((category) => {
        if (category.id === editModal?.id) {
          return { ...category, ...body };
        } else return category;
      });

      return { ...prev, categories: updated };
    });

    emptyForm();
    setEditModal(null);
  }

  async function handleAddCategory(
    name: BTCategoryResponse['name'],
    description: BTCategoryResponse['description'],
    emptyForm: () => void
  ) {
    const uuid = Math.floor(Math.random() * 1000000);

    const body: BTCategoryResponse = {
      name,
      description,
      id: Number(uuid),
      items: [],
    };

    if (!name) return ErrorToast('Please enter category name');

    // If adding a new modifier group, add it to the appropriate category
    setMenu((prev) => {
      if (!prev) return prev;
      const updated = [...prev.categories, body];

      return { ...prev, categories: updated };
    });

    emptyForm();
    setAddModal(false);
  }

  async function handleDeleteCategory() {
    setMenu((prev) => {
      if (!prev) return prev;

      const updated = prev.categories.filter((item) => item.id !== deleteModal);

      return { ...prev, categories: updated };
    });

    setDeleteModal(null);
  }

  const AddCategoryButton = () => {
    return (
      <Button $wSM="200px" $selected onClick={() => setAddModal(true)}>
        Add Category
      </Button>
    );
  };

  const [activeId, setActiveId] = useState(null);

  function dragEndEvent(e: any) {
    const { over, active } = e;
    moveItem({ over, active });
  }

  const moveItem = ({
    over,
    active,
  }: {
    over: { id: number };
    active: { id: number };
  }) => {
    const items = menu?.categories;
    if (!items) return;

    const sortedCategories = arrayMove(
      items,
      items.findIndex((category) => category.id === active.id),
      items.findIndex((category) => category.id === over?.id)
    );

    setMenu((prev) => {
      if (!prev) return prev;
      return { ...prev, categories: sortedCategories };
    });
  };

  function handleDragStart(event: any) {
    setActiveId(event.active.id);
  }

  function handleDragEnd(event: any) {
    if (event.over) {
      setActiveId(null);
      dragEndEvent(event);
    }
  }

  return (
    <Div>
      <Div $flex $between $alignCenter $mb="20">
        <H3 $mb="0">Categories ({menu?.categories.length})</H3>
        {menu?.categories && menu?.categories?.length > 0 && (
          <Icon $pointer onClick={() => setAddModal(true)}>
            <PlusIcon />
          </Icon>
        )}
      </Div>

      <DndContext
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        // modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={menu?.categories?.map((task) => task.id) || []}
          strategy={verticalListSortingStrategy}
          // disabled={sortingDisabled}
        >
          {menu?.categories?.map((item) => (
            <CategoryComponent
              key={item.id}
              item={item}
              onClick={(item) => setEditModal(item)}
              onClickDelete={(id) => setDeleteModal(id)}
              // disabled={sortingDisabled}
            />
          ))}
        </SortableContext>
        <DragOverlay>
          {activeId ? (
            <CategoryComponent
              item={
                menu?.categories?.find(
                  (t) => activeId === t.id
                ) as BTCategoryResponse
              }
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {menu?.categories && menu?.categories?.length < 1 && (
        <Div $flex $column $alignCenter>
          <Img alt="" $width="180px" src={noCategories} $mb="24" />
          <H3 $mb="24" $center>
            No Categories
          </H3>
          <P $mb="32" $center>
            This provider currently don’t have <br /> menu categories.
          </P>
          <AddCategoryButton />
        </Div>
      )}

      <AddCategoryModal
        addModal={addModal}
        closeModal={() => {
          setAddModal(false);
        }}
        handleAddCategory={handleAddCategory}
      />

      <AddCategoryModal
        addModal={editModal}
        closeModal={() => {
          setEditModal(null);
        }}
        handleEditCategory={handleEditCategory}
        edit
      />

      <DeleteCategoryModal
        deleteModal={!!deleteModal}
        closeModal={() => {
          setDeleteModal(null);
        }}
        handleDeleteCategory={handleDeleteCategory}
      />
    </Div>
  );
};
